import type {Nominal} from "../types";
import {wsServerHost} from "../utils/server-hosts";
import {defineRepo, defineTables, defineValues, oneOffSync} from "./db";
import type {GroupId} from "./GroupRepo";
import {USER_REPO_VERSION} from "./repo-versions";

export type UserId = Nominal<string, "user">;
export type UserRepo = ReturnType<typeof UserRepoLoader.useRepo>;

export type GroupPreviewEntry = {
  id: GroupId;
  shortId: string;
  name: string;
  joinedAt: Date;
};

export const UserRepoLoader = defineRepo({
  key: "user",
  version: USER_REPO_VERSION,
  tables: defineTables().add(
    "group",
    {
      id: {type: "string"},
      shortId: {type: "string"},
      name: {type: "string"},
      joinedAt: {type: "number"},
    },
    {
      hydrate: (row): GroupPreviewEntry => ({
        id: row.id! as GroupId,
        shortId: row.shortId!,
        name: row.name!,
        joinedAt: new Date(row.joinedAt!),
      }),
      dehydrate: (model) => ({
        ...model,
        joinedAt: model.joinedAt.getTime(),
      }),
    }
  ),
  values: defineValues({
    schema: {
      id: {type: "string"},
      name: {type: "string"},
      createdAt: {type: "number"},
      isSyncing: {type: "boolean"},
    },
    hydrate: (row) => ({
      id: row.id! as UserId,
      name: row.name,
      createdAt: new Date(row.createdAt!),
      isSyncing: row.isSyncing!,
    }),
    dehydrate: (model) => ({
      ...model,
      createdAt: model.createdAt.getTime(),
    }),
  }),
  migrate: (repo, fromVersion) => {
    switch (fromVersion) {
      case 0:
        return;
    }
  },
  syncInfo: {
    get: (store) => {
      const {id, isSyncing} = store.getValues();
      return isSyncing && id ? `${wsServerHost}/sync/user/${id}` : null;
    },
    use: (repo) => {
      const {id, isSyncing} = repo.vals.useValues();
      return isSyncing ? `${wsServerHost}/sync/user/${id}` : null;
    },
  },
});

export const syncUserRepo = async (userId: string, userRepo: UserRepo) => {
  await oneOffSync(`${wsServerHost}/sync/user/${userId}`, userRepo.store);
};
